// src/components/Sales/POSSales.js
import React, { useState, useEffect } from 'react';

const POSSales = ({ sales, recipes, menuItems, onDeleteSale }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredSales, setFilteredSales] = useState([]);

  // 🔹 Filtrar solo ventas POS
  const posSales = Array.isArray(sales)
    ? sales.filter(sale => sale.source === 'pos')
    : [];

  useEffect(() => {
    let current = posSales;

    if (startDate) {
      const startTimestamp = new Date(startDate).getTime();
      current = current.filter(
        sale => new Date(sale.sale_date).getTime() >= startTimestamp
      );
    }

    if (endDate) {
      const endTimestamp = new Date(endDate).getTime();
      current = current.filter(
        sale => new Date(sale.sale_date).getTime() <= endTimestamp + 86400000
      );
    }

    setFilteredSales(current);
  }, [sales, startDate, endDate]);

  // 🔹 Totales
  const totalCostOfSales = filteredSales.reduce((total, sale) => {
    const recipe = Array.isArray(recipes)
      ? recipes.find(rec => rec.productId === sale.product_id)
      : undefined;
    if (recipe && recipe.servings > 0) {
      return total + (recipe.totalCost / recipe.servings) * (sale.quantitySold || 0);
    }
    return total;
  }, 0);

  const totalWithoutVAT = filteredSales.reduce(
    (total, sale) => total + (sale.priceWithoutVAT || 0) * (sale.quantitySold || 0),
    0
  );

  const totalWithVAT = filteredSales.reduce(
    (total, sale) => total + (sale.price || 0) * (sale.quantitySold || 0),
    0
  );

  const handleDelete = (sale) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta venta?')) return;
    if (typeof onDeleteSale === 'function') onDeleteSale(sale.order_id);
  };

  return (
    <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold text-[#3F1907] mb-3">
        Ventas del Punto de Venta
      </h3>

      {/* 🔹 Filtros de fechas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-[#3F1907] text-sm font-bold mb-1">
            Fecha Inicio
          </label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          />
        </div>
        <div>
          <label className="block text-[#3F1907] text-sm font-bold mb-1">
            Fecha Fin
          </label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={() => { setStartDate(''); setEndDate(''); }}
            className="w-full bg-gray-500 hover:bg-gray-600 text-[#F9EFD7] font-medium py-2 px-4 rounded-md transition duration-200"
          >
            Limpiar Filtro
          </button>
        </div>
      </div>

      {/* 🔹 Historial */}
      {filteredSales.length === 0 ? (
        <p className="text-[#3F1907]">
          No hay ventas registradas en este rango de fechas.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#58735A]">
            <thead className="bg-[#58735A]">
              <tr>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Fecha</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Producto</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Precio (con IVA)</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Precio (sin IVA)</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Cantidad</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Total</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Pago</th>
                <th className="px-3 py-2 text-xs text-center text-[#F9EFD7]">Acciones</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9EFD7] divide-y divide-[#58735A]">
              {filteredSales.map((sale, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    {new Date(sale.sale_date).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    {sale.name || 'Producto eliminado'}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    ${Number(sale.price).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    ${Number(sale.priceWithoutVAT).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    {sale.quantitySold}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    ${Number(sale.total).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-[#3F1907]">
                    {sale.payment_method}
                  </td>
                  <td className="px-3 py-2 text-sm text-center">
                    {sale.order_id ? (
                      <button
                        onClick={() => handleDelete(sale)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Eliminar
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="7" className="px-4 py-3 text-right text-sm font-medium text-[#3F1907]">
                  Total General (con IVA):
                </td>
                <td className="px-4 py-3 text-sm font-bold text-[#3F1907]">
                  ${totalWithVAT.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan="7" className="px-4 py-3 text-right text-sm font-medium text-[#3F1907]">
                  Total General (sin IVA):
                </td>
                <td className="px-4 py-3 text-sm font-bold text-[#3F1907]">
                  ${totalWithoutVAT.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td colSpan="7" className="px-4 py-3 text-right text-sm font-medium text-[#3F1907]">
                  Costo Total de Ventas:
                </td>
                <td className="px-4 py-3 text-sm font-bold text-[#3F1907]">
                  ${totalCostOfSales.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default POSSales;